import { DOCUMENT, inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { appendToRmaBaseUrl, appendToRmaCdnUrl, appendToRmaStaticImageUrl } from '@rma/generic/ng/util';
import { IS_PRODUCTION, PlatformService } from '@rma/public/generic/util/environment';
import { filter, map, mergeMap } from 'rxjs';
import { BaseMetaData, LinkingData, LinkingDataTypes, MetaInfo, MetaNoIndex, MetaTag, SocialTagConfig } from '../domain';
import { LinkingDataService } from '../util-linking-data/linking-data.service';
import { MetaTagService } from '../util-meta-tag-service/meta-tag.service';

@Injectable({
  providedIn: 'root',
})
export class MetaService {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly dom = inject<Document>(DOCUMENT);
  private readonly linkingDataService = inject(LinkingDataService);
  private readonly meta = inject(Meta);
  private readonly metaTagService = inject(MetaTagService);
  private readonly platformService = inject(PlatformService);
  private readonly rmaBaseUrl = inject(appendToRmaBaseUrl);
  private readonly rmaImageUrl = inject(appendToRmaStaticImageUrl);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly cdnUrl = inject(appendToRmaCdnUrl);

  public readonly defaultImage = this.cdnUrl('suburb-default.jpg');
  public readonly defaultOgImage = this.rmaImageUrl(`opengraph/og_img_2.jpg`);

  public update(
    baseMetaData: BaseMetaData | null = null,
    linkingData: LinkingData | null = null,
    socialConfig: Partial<SocialTagConfig> | null = null,
  ): void {
    if (baseMetaData === null) {
      this.pushNoIndex();
      return;
    }

    // We only care about the extended tags on the server, not in the browser
    if (this.platformService.isPlatformServer || !IS_PRODUCTION) {
      this.updateAllTags(baseMetaData, socialConfig, linkingData);
    } else {
      this.updateTitle(baseMetaData.title);
    }
  }

  public noIndex(metaNoIndex?: MetaNoIndex) {
    this.pushNoIndex(metaNoIndex?.nofollow);

    if (metaNoIndex?.title) {
      this.updateTitle(metaNoIndex.title);
    }
  }

  public watchRouting(): void {
    // is this neccessary?
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
      )
      .subscribe(({ title, description, robots }) => {
        if (title) {
          this.updateTitle(title);
        }
        if (description) {
          this.updateMetaTag({ content: description, name: 'description' });
        }
        // Set-only: NavigationEnd fires after components construct, so clearing would undo their noIndex().
        if (typeof robots === 'string' && robots) {
          this.updateMetaTag({ content: robots, name: 'robots' });
        }
      });
  }

  private updateAllTags(baseMetaData: BaseMetaData, social: Partial<SocialTagConfig> | null, linkingData: Partial<LinkingData> | null) {
    const { author, keywords, nextUrl, prevUrl, description, title } = baseMetaData;
    const canonicalUrl = this.getCanonicalUrl(baseMetaData.canonicalUrl);
    const image = baseMetaData.imageUrl ?? this.defaultOgImage;

    const socialData = {
      author,
      description,
      image,
      keywords,
      title,
      url: canonicalUrl,
    };
    const metaTags = this.metaTagService.generateMetaTags(socialData, social ?? {});
    const ldSchema = this.linkingDataService.generateLdSchema(linkingData);
    const metaInfo = {
      canonicalUrl,
      ldSchema,
      metaTags,
      nextUrl,
      title,
      previousUrl: prevUrl,
    };

    this.pushInfo(metaInfo);
  }

  private pushInfo(metaInfo: MetaInfo): void {
    this.updateTitle(metaInfo.title);
    this.updateMetaTags(metaInfo.metaTags ?? []);
    this.updateCanonicalURL(metaInfo.canonicalUrl);
    this.updateNextOrPreviousURL(metaInfo.nextUrl ?? '', 'next');
    this.updateNextOrPreviousURL(metaInfo.previousUrl ?? '', 'previous');

    if (metaInfo.ldSchema) {
      this.updateLinkingData(metaInfo.ldSchema);
    }
  }

  private pushNoIndex(nofollow?: boolean): void {
    this.updateMetaTag({ content: nofollow ? 'noindex, nofollow' : 'noindex', name: 'robots' });
  }

  private getCanonicalUrl(canonicalUrl?: string) {
    if (canonicalUrl) {
      return (canonicalUrl.startsWith('http') ? canonicalUrl : this.rmaBaseUrl(canonicalUrl)).split(/[?#]/)[0];
    }

    return this.dom.URL.replace('http://', 'https://').split(/[?#]/)[0];
  }

  private updateTitle(baseTitle: string): void {
    this.title.setTitle(baseTitle);
  }

  private updateMetaTags(metaTags: MetaTag[]): void {
    const tags = metaTags.filter(({ content }) => Boolean(content));
    this.meta.addTags(tags, true);
  }

  private updateMetaTag(metaTag: MetaTag): void {
    if (!metaTag.content) {
      return;
    }
    this.meta.updateTag({ ...metaTag });
  }

  private updateCanonicalURL(url?: string): void {
    const canUrl = url ? url : this.dom.URL;
    const selector = "link[rel='canonical']";
    const rel = 'canonical';

    this.updateHeadLink(selector, rel, canUrl);
  }

  private updateNextOrPreviousURL(url: string, nextOrPrevious: string): void {
    const selector = `link[rel='${nextOrPrevious}']`;
    const rel = nextOrPrevious;

    this.updateHeadLink(selector, rel, url);
  }

  private updateHeadLink(selector: string, rel: string, url?: string): void {
    const existingLink = this.dom.querySelector(selector);

    if (!url) {
      if (existingLink) {
        existingLink.remove();
      }
      return;
    }

    if (existingLink) {
      existingLink.setAttribute('href', url);
    } else {
      const link: HTMLLinkElement = this.dom.createElement('link');
      link?.setAttribute('rel', rel);
      link?.setAttribute('href', url);
      this.dom.head?.appendChild(link);
    }
  }

  private updateLinkingData(schema?: LinkingDataTypes[]) {
    const existingTag = this.dom.querySelector("script[type='application/ld+json']");
    if (existingTag) {
      existingTag.remove();
    }
    if (schema) {
      // safari 16.5+ has issues with passing @context in arrays. We probably need to add a higher level @context and @type
      if (Array.isArray(schema)) {
        schema.forEach((data) => this.addLinkingDataToDom(data));
      } else {
        this.addLinkingDataToDom(schema);
      }
    }
  }

  private addLinkingDataToDom(data: LinkingDataTypes) {
    if (data) {
      const ldJsonScript = this.dom.createElement('script');
      ldJsonScript.setAttribute('type', 'application/ld+json');
      ldJsonScript.innerHTML = JSON.stringify(data, null, 1);
      this.dom.head?.appendChild(ldJsonScript);
    }
  }
}
